import { Fragment } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';

import { dateLabel, t } from '../i18n';
import { Icon, initials } from './icons';
import { EmptyState, StatusBadge, TypeLabel } from './MyFeedback';

import type { ApiClient } from '../api';
import type { FeedbackDetail, Paragraph, Selection } from '../types';

function Text({ paragraph }: { paragraph: Paragraph }) {
  return (
    <p>
      {paragraph.map((span, i) => {
        let node = <>{span.text}</>;
        if (span.italic) node = <em>{node}</em>;
        if (span.bold) node = <strong>{node}</strong>;
        if (span.href)
          node = (
            <a href={span.href} target="_blank" rel="noopener noreferrer">
              {node}
            </a>
          );
        return <Fragment key={i}>{node}</Fragment>;
      })}
    </p>
  );
}

function captureLabel(selection: Selection | null, pasted: boolean) {
  const s = t();
  if (pasted) return s.pastedImage;
  if (!selection) return s.screenshot;
  return selection.kind === 'element'
    ? `${s.elementLabel} ${selection.description}`
    : `${s.areaLabel} ${Math.round(selection.rect.width)} × ${Math.round(selection.rect.height)} px`;
}

/** One report: what the tester sent, and the conversation with the team about it. */
export function Thread({
  api,
  email,
  id,
  onBack,
  onRead,
}: {
  api: ApiClient;
  email: string;
  id: string;
  onBack: () => void;
  onRead: () => void;
}) {
  const s = t();
  const [detail, setDetail] = useState<FeedbackDetail | null>(null);
  const [failed, setFailed] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [replyFailed, setReplyFailed] = useState(false);
  const firstNew = useRef<HTMLDivElement>(null);
  const end = useRef<HTMLDivElement>(null);

  const load = async () => {
    setFailed(false);
    try {
      setDetail(await api.detail(email, id));
      onRead();
    } catch {
      setFailed(true);
    }
  };

  useEffect(() => {
    void load();
  }, [id]);

  // Open at the first new message, or at the end of the conversation.
  useEffect(() => {
    (firstNew.current ?? end.current)?.scrollIntoView({ block: 'nearest' });
  }, [detail?.messages.length]);

  const send = async () => {
    const body = reply.trim();
    if (!body) return;
    setSending(true);
    try {
      const message = await api.reply(email, id, body);
      setDetail((d) => (d ? { ...d, messages: [...d.messages, message] } : d));
      setReply('');
      setReplyFailed(false);
    } catch {
      setReplyFailed(true);
    } finally {
      setSending(false);
    }
  };

  const back = (
    <button type="button" class="hfb-back" onClick={onBack}>
      <Icon name="chevronLeft" />
      {s.back}
    </button>
  );

  if (failed) {
    return (
      <div class="hfb-thread">
        {back}
        <EmptyState
          icon="alert"
          tone="danger"
          title={s.loadFailedTitle}
          body={s.loadFailedBody}
          action={{ label: s.tryAgain, primary: true, onClick: () => void load() }}
        />
      </div>
    );
  }
  if (!detail) {
    return (
      <div class="hfb-thread">
        {back}
        <p class="hfb-muted">…</p>
      </div>
    );
  }

  const { item, messages, seenBefore } = detail;
  const newIndex = messages.findIndex(
    (m) => m.authorKind === 'team' && (!seenBefore || m.createdAt > seenBefore),
  );

  return (
    <div class="hfb-thread">
      <div class="hfb-thread-scroll">
        {back}
        <header class="hfb-thread-head">
          <span class="hfb-row-meta">
            <TypeLabel type={item.type} />
            <span class="hfb-muted">
              {s.sent} {dateLabel(item.createdAt)}
            </span>
            <StatusBadge status={item.status} />
          </span>
          <h2 class="hfb-display">{item.title}</h2>
        </header>

        <details class="hfb-card hfb-thread-story" open>
          <summary>
            <Icon name="chevronRight" class="hfb-chevron" />
            {s.whatYouSent}
          </summary>
          <div class="hfb-thread-story-body">
            {detail.story.map((block, i) =>
              block.kind === 'text' ? (
                block.paragraphs.map((p, j) => <Text key={`${i}-${j}`} paragraph={p} />)
              ) : (
                <span key={i} class="hfb-chip">
                  <Icon name="image" size={14} />
                  {captureLabel(block.selection, block.pasted)}
                </span>
              ),
            )}
          </div>
        </details>

        <div class="hfb-messages" aria-live="polite">
          {messages.length === 0 && (
            <EmptyState icon="chat" title={s.noMessagesTitle} body={s.noMessagesBody} />
          )}
          {messages.map((m, i) => {
            const mine = m.authorKind === 'reporter';
            return (
              <Fragment key={m.id}>
                {i === newIndex && newIndex > 0 && (
                  <div class="hfb-new-divider" ref={firstNew}>
                    <span>{s.newDivider}</span>
                  </div>
                )}
                <article class={`hfb-message ${mine ? 'is-mine' : 'is-team'}`}>
                  <header>
                    <b>{mine ? s.you : m.authorName}</b>
                    <span>
                      {mine ? '' : ' · Hoppinger'} · {dateLabel(m.createdAt, true)}
                    </span>
                  </header>
                  <div class="hfb-message-row">
                    {!mine && (
                      <span class="hfb-avatar" aria-hidden="true">
                        {initials(m.authorName)}
                      </span>
                    )}
                    <p class="hfb-bubble">{m.body}</p>
                  </div>
                </article>
              </Fragment>
            );
          })}
          <div ref={end} />
        </div>
      </div>

      <form
        class="hfb-reply"
        onSubmit={(event) => {
          event.preventDefault();
          void send();
        }}
      >
        <label class="hfb-label" for="hfb-reply">
          {s.reply}
        </label>
        <textarea
          id="hfb-reply"
          class="hfb-input"
          rows={3}
          maxLength={5000}
          value={reply}
          placeholder={s.replyPlaceholder}
          onInput={(e) => setReply(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void send();
            }
          }}
        />
        {replyFailed && <p class="hfb-field-error">{s.replyFailed}</p>}
        <div class="hfb-reply-actions">
          <span class="hfb-hint">
            <kbd>⌘</kbd> <kbd>Enter</kbd> {s.toSend}
          </span>
          <button type="submit" class="hfb-btn hfb-btn-primary" disabled={sending || !reply.trim()}>
            {s.sendReply}
          </button>
        </div>
      </form>
    </div>
  );
}
